import React, { useState } from 'react';
import AlertPopup from "../../components/common/AlertPopup";
import {
  Box,
  Grid,
  Typography,
  InputLabel,
  FormGroup,
} from '@mui/material';

import {
  CustomTextField,
  Label
} from "../Worker/Create/styles";
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { useEffect } from "react";
import PageCard from "../../components/PageCard";
import { useSidebar } from "../../context/SidebarContext";
import { useBreadcrumb } from '../../context/BreadcrumbContext';
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from 'react-hook-form';
import { ProfileFormInputs } from "../../interfaces";
import { yupResolver } from '@hookform/resolvers/yup';
import FacilityofficeIcon from '../../assets/facility/facility-office.svg';
import FacilityDocument from './components/FacilityDocument'; // Changed from UserProfileTab
import {
  CustomCard,
  SummaryIconWrapper,
  RequiredAsterisk,
  ErrorMessage,
  StyledTabTitle,
  StatusChip,
  PrimaryButton,
  CancelButton,
} from './styles';
import * as yup from 'yup';
import { AppDispatch } from "../../store";
import {
  selectFacilityTypeDocumentRequirements,
  selectFacilityTypeDocumentRequirementsLoading
} from '../../modules/facility/selectors';
import { selectFacilityProfile } from '../../modules/facilityProfile/selectors';
import { fetchFacilityTypes, updateFacilityProfile, fetchFacilityTypeDocumentRequirements } from '../../modules/facility/actions';
import { fetchFacilityProfile } from '../../modules/facilityProfile/actions';


const schema = yup.object().shape({
  facility_type: yup.number().required('Facility type is required').min(1, 'Please select a facility type'),
  orgName: yup.string().required('Facility name is required'),
  email: yup.string().matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email address').email('Invalid email').required('Email is required'),
  phoneNumber: yup.string().matches(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number').required('Phone number is required'),
  contactPerson: yup.string().required('Contact person is required'),
  designation: yup.string().required('Designation is required'),
  address: yup.string().required('Address is required'),
});


// --- Main Document Detail Screen Component ---
const FacilityProfilePage: React.FC = () => {

  const facilityProfile = useSelector(selectFacilityProfile);
  console.log('DEBUG: facilityProfile', facilityProfile);
  const facilityTypeDocumentRequirements = useSelector(selectFacilityTypeDocumentRequirements);
  const isLoadingFacilityTypeDocumentRequirements = useSelector(selectFacilityTypeDocumentRequirementsLoading);
  const [facilityTypeName, setFacilityTypeName] = useState<string>('');
  const dispatch = useDispatch<AppDispatch>();
  const [status, setStatus] = useState<string>('');

  useEffect(() => {
    dispatch(fetchFacilityTypes({ page: 1, page_size: 100 }));
    dispatch(fetchFacilityProfile());
  }, [dispatch]);

  useEffect(() => {
    if (facilityProfile?.facility_type_name) {
      setFacilityTypeName(facilityProfile.facility_type_name);
      setStatus(facilityProfile.status);
    }
  }, [facilityProfile]);

  const facilityTypeId = typeof facilityProfile?.facility_type === 'object'
    ? (facilityProfile.facility_type as any).facility_type_id
    : facilityProfile?.facility_type;

  useEffect(() => {
    if (facilityTypeId) {
      dispatch(fetchFacilityTypeDocumentRequirements({ facility_type_id: Number(facilityTypeId) }));
    }
  }, [dispatch, facilityTypeId]);

  const documentList = React.useMemo(() => {
    if (facilityTypeDocumentRequirements && facilityProfile) {
      return facilityTypeDocumentRequirements.map((requirement: any) => {
        // Use string conversion to safely handle string/number mismatches
        const uploadedDocument = facilityProfile.uploaded_documents.find(
          (doc: any) => String(doc.document_type) === String(requirement.requirement_id)
        );
        return {
          ...requirement,
          uploaded_document: uploadedDocument || null,
        };
      });
    }
    return [];
  }, [facilityTypeDocumentRequirements, facilityProfile]);

  const { setPath, setIsShow } = useBreadcrumb();
  const { setActivePage } = useSidebar();

  const { handleSubmit, control, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setPath([
      { title: 'Dashboard', path: '/' },
      { title: 'Profile', path: '' },
    ]);
    setActivePage('profile');
    setIsShow(true);
  }, [dispatch, setPath, setActivePage, setIsShow]);

  useEffect(() => {
    if (facilityProfile) {
      reset({
        orgName: facilityProfile.facility_name,
        facility_type: typeof facilityProfile.facility_type === 'object' ? (facilityProfile.facility_type as any).facility_type_id : facilityProfile.facility_type,
        email: facilityProfile.email || '',
        phoneNumber: facilityProfile.phone_number || '',
        contactPerson: facilityProfile.contact_person || '',
        designation: facilityProfile.contact_person_designation || '',
        address: facilityProfile.address || '',
      });
    }
  }, [facilityProfile, reset]);

  // Notification State
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success', // Default
  });

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };


  const onSubmit = async (data: ProfileFormInputs) => {
    const payload = {
      facility_name: data.orgName,
      address: data.address,
      user_phone_number: data.phoneNumber || '',
      facility_type: data.facility_type,
      contact_person: data.contactPerson || '',
      contact_person_designation: data.designation || '',
    };
    const result = await dispatch(updateFacilityProfile(payload));
    if (result.meta.requestStatus === 'fulfilled') {
      setNotification({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success',
      });
    } else {
      setNotification({
        open: true,
        message: 'Failed to update profile. Please try again.',
        severity: 'error',
      });
    }
  };


  const getSatusColor = (status: string | undefined): 'success' | 'warning' | 'error' | 'default' => {
    if (status === 'verified' || status === 'Verified' || status === 'Approved' || status === 'approved') {
      return 'success';
    } else if (status === 'pending') {
      return 'warning';
    } else if (status === 'blocked') {
      return 'error';
    } else {
      return 'default';
    }
  }

  const handleRefresh = () => {
    dispatch(fetchFacilityProfile());
  };

  const handleDocumentSuccess = () => {
    setNotification({
      open: true,
      message: 'Document uploaded successfully!',
      severity: 'success',
    });
    handleRefresh();
  };

  const handleDocumentError = (message: string) => {
    setNotification({
      open: true,
      message: message || 'Failed to upload document.',
      severity: 'error',
    });
  };

  return (
    <Box >
      {/* Header Section */}
      <PageCard padding={2} backgroundRequired={true}>
        <CustomCard sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center">
            <SummaryIconWrapper>
              <img src={FacilityofficeIcon} alt='facility-office' />
            </SummaryIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {facilityProfile?.facility_name}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                ID: {facilityProfile?.facility_id}
              </Typography>
            </Box>
          </Box>
          {status &&
            <StatusChip
              icon={<CheckCircleOutlineRoundedIcon />}
              status={(status) ? status : "Pending"}
              label={status}
              size="small"
              color={getSatusColor(status)}
            />
          }
        </CustomCard>
      </PageCard>

      {/* Basic Details Section */}
      <PageCard padding={3} backgroundRequired={true}>

        <StyledTabTitle>
          Basic Details
        </StyledTabTitle>
        <form onSubmit={handleSubmit(onSubmit)}>

          <Box padding={2}>
            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <FormGroup>
                  <Label>Legal Name</Label>
                  <Controller
                    name="orgName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Enter Legal Name"
                        error={!!errors.orgName}
                        helperText={errors.orgName?.message}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <InputLabel htmlFor="facility_type" sx={{ mb: 1, fontWeight: 500 }}>
                  Type<RequiredAsterisk>*</RequiredAsterisk>
                </InputLabel>
                <CustomTextField
                  fullWidth
                  value={facilityTypeName}
                  disabled
                />
              </Grid>

              {/* Row 2: Address */}
              <Grid size={{ xs: 12, md: 12 }}>
                <FormGroup>
                  <InputLabel htmlFor="address" sx={{ mb: 1, fontWeight: 500 }}>
                    Address<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <Controller
                    name="address"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Address"
                        error={!!errors.address}
                        helperText={errors.address?.message}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormGroup>
                  <InputLabel htmlFor="contactPerson" sx={{ mb: 1, fontWeight: 500 }}>
                    Contact Person<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <Controller
                    name="contactPerson"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Contact Person"
                        error={!!errors.contactPerson}
                        helperText={errors.contactPerson?.message}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormGroup>
                  <InputLabel htmlFor="designation" sx={{ mb: 1, fontWeight: 500 }}>
                    Designation<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <Controller
                    name="designation"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Designation"
                        error={!!errors.designation}
                        helperText={errors.designation?.message}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormGroup>
                  <InputLabel htmlFor="phoneNumber" sx={{ mb: 1, fontWeight: 500 }}>
                    Phone Number<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Phone Number"
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                      />
                    )}
                  />
                  {errors.phoneNumber && <ErrorMessage>{errors.phoneNumber.message}</ErrorMessage>}

                </FormGroup>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormGroup>
                  <InputLabel htmlFor="emailAddress" sx={{ mb: 1, fontWeight: 500 }}>
                    Email Address<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Email Address"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={true}
                      />
                    )}
                  />
                  {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
          <StyledTabTitle>
            Uploaded Documents
          </StyledTabTitle>
          <Box padding={2}>
            {isLoadingFacilityTypeDocumentRequirements ? (
              <Typography>Loading...</Typography>
            ) : (
              documentList.map((doc: any) => (
                <FacilityDocument
                  key={doc.requirement_id}
                  document={doc}
                  onRefresh={handleRefresh}
                  onSuccess={handleDocumentSuccess}
                  onError={handleDocumentError}
                />
              ))
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, padding: 2 }}>
            <CancelButton variant="outlined" sx={{ mr: 2 }} onClick={() => reset()}>
              Cancel
            </CancelButton>
            <PrimaryButton type="submit" variant="contained">
              Submit
            </PrimaryButton>
          </Box>
        </form>
      </PageCard>

      <AlertPopup
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />
    </Box>
  );
};

export default FacilityProfilePage;